import { useState, useEffect, useRef } from 'react'
import type { RefObject } from 'react'
import type { AccountWithStats } from '@olimhub/shared'
import { searchApi } from '@/api'

interface UseMentionAutocompleteReturn {
  showDropdown: boolean
  results: AccountWithStats[]
  selectedIdx: number
  handleChange: (text: string, cursorPos: number) => void
  handleKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void
  selectMention: (username: string) => void
  closeMention: () => void
}

export function useMentionAutocomplete(
  content: string,
  setContent: (v: string) => void,
  textareaRef: RefObject<HTMLTextAreaElement | null>,
): UseMentionAutocompleteReturn {
  const [mentionQuery, setMentionQuery] = useState<string | null>(null)
  const [mentionStart, setMentionStart] = useState(-1)
  const [results, setResults] = useState<AccountWithStats[]>([])
  const [selectedIdx, setSelectedIdx] = useState(0)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  function detectMention(text: string, cursorPos: number) {
    const before = text.slice(0, cursorPos)
    // Match @ preceded by whitespace, newline, or start of string
    const match = /(?:^|[\s\n])@([a-zA-Z0-9_]*)$/.exec(before)
    if (match) {
      const atIdx = before.lastIndexOf('@')
      setMentionStart(atIdx)
      setMentionQuery(match[1]!)
      setSelectedIdx(0)
    } else {
      closeMention()
    }
  }

  useEffect(() => {
    if (mentionQuery === null) { setResults([]); return }
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      try {
        const data = await searchApi.searchAccounts(mentionQuery)
        setResults(data.items.slice(0, 5))
      } catch { setResults([]) }
    }, 200)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [mentionQuery])

  function selectMention(username: string) {
    const ta = textareaRef.current
    if (!ta) return
    const cursorPos = ta.selectionStart
    const before = content.slice(0, mentionStart)
    const after = content.slice(cursorPos)
    const newContent = `${before}@${username} ${after}`
    setContent(newContent)
    closeMention()
    requestAnimationFrame(() => {
      const newPos = before.length + username.length + 2
      ta.focus()
      ta.setSelectionRange(newPos, newPos)
    })
  }

  function closeMention() {
    setMentionQuery(null)
    setMentionStart(-1)
    setResults([])
  }

  function handleChange(text: string, cursorPos: number) {
    detectMention(text, cursorPos)
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (!results.length && mentionQuery === null) return
    if (e.key === 'Escape') { e.preventDefault(); closeMention(); return }
    if (!results.length) return
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIdx((i) => Math.min(i + 1, results.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIdx((i) => Math.max(i - 1, 0)) }
    else if (e.key === 'Enter' && results[selectedIdx]) { e.preventDefault(); selectMention(results[selectedIdx]!.username) }
    else if (e.key === ' ') { closeMention() }
  }

  return {
    showDropdown: mentionQuery !== null && results.length > 0,
    results,
    selectedIdx,
    handleChange,
    handleKeyDown,
    selectMention,
    closeMention,
  }
}
